import React, { useEffect, useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import ThemeToggle from '../components/ThemeToggle';
import AudioQrCard from '../components/AudioQrCard';
import AffirmationAudioRecorder from '../components/AffirmationAudioRecorder';
import RmPasswordSetup from '../components/RmPasswordSetup';
import SolutgenSupportWidget from '../components/SolutgenSupportWidget';
import { configureAudioRmPassword, getAudioSubmission, saveAudioPhrases } from '../lib/api';
import {
  AFFIRMATION_AUDIO_INTRO,
  AFFIRMATION_AUDIO_OUTRO,
  createAffirmationScript,
  getAudioStatusLabel,
  normalizePhrases,
  validatePhrases,
} from '../lib/affirmationAudio';
import { CheckCircle, Copy, Loader2, Save, ShieldAlert, Volume2 } from 'lucide-react';

const MAX_PHRASES = 6;

const getTokenFromUrl = () => new URLSearchParams(window.location.search).get('token') || '';

export function AudioPage() {
  const [token] = useState(getTokenFromUrl);
  const [audioState, setAudioState] = useState(null);
  const [phrases, setPhrases] = useState(Array(MAX_PHRASES).fill(''));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const audioUrl = token ? `${window.location.origin}/audio?token=${encodeURIComponent(token)}` : '';
  const cleanPhrases = useMemo(() => normalizePhrases(phrases).filter(Boolean), [phrases]);
  const scriptText = audioState?.script?.full_text || createAffirmationScript(cleanPhrases);
  const hasUploadedAudio = audioState?.status === 'recording_uploaded' || audioState?.status === 'submitted_to_editor';

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex,nofollow');
  }, []);

  useEffect(() => {
    const loadAudio = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const result = await getAudioSubmission(token);
        setAudioState(result);
        const existingPhrases = result.phrases?.length ? result.phrases : Array(MAX_PHRASES).fill('');
        setPhrases([...existingPhrases, ...Array(MAX_PHRASES).fill('')].slice(0, MAX_PHRASES));
      } catch (error) {
        setAudioState(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadAudio();
  }, [token]);

  const updatePhrase = (index, value) => {
    const next = [...phrases];
    next[index] = value;
    setPhrases(next);
  };

  const handleSavePhrases = async () => {
    const validation = validatePhrases(phrases);
    if (validation) {
      toast.error(validation);
      return;
    }
    setIsSaving(true);
    try {
      const result = await saveAudioPhrases(token, normalizePhrases(phrases).filter(Boolean));
      setAudioState(result);
      setPhrases([...result.phrases, ...Array(MAX_PHRASES).fill('')].slice(0, MAX_PHRASES));
      toast.success('Frases guardadas');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudieron guardar las frases');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyScript = async () => {
    try {
      await navigator.clipboard.writeText(scriptText);
      toast.success('Texto copiado');
    } catch (error) {
      toast.error('No se pudo copiar el texto');
    }
  };

  const handleUploaded = (result) => {
    setAudioState(result);
  };

  const handleCreateRmPassword = async (password) => (
    configureAudioRmPassword(token, password)
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!token || !audioState) {
    return (
      <div className="min-h-screen bg-background">
        <Toaster richColors position="top-center" />
        <main className="max-w-xl mx-auto px-4 py-16">
          <Card className="border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900">
            <CardHeader className="text-center">
              <ShieldAlert className="w-12 h-12 text-amber-600 mx-auto" />
              <CardTitle>Link no disponible</CardTitle>
              <CardDescription>
                Para grabar el audio necesitás entrar desde el link generado en el formulario.
              </CardDescription>
            </CardHeader>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster richColors position="top-center" />
      <SolutgenSupportWidget
        user={{
          id: audioState.submission_id || audioState.user?.email || 'audio-student',
          name: audioState.user?.name || 'Alumno',
          email: audioState.user?.email || '',
          role: 'student'
        }}
        context={{
          module: window.location.pathname,
          submissionId: audioState.submission_id || '',
          audioStatus: audioState.status || ''
        }}
      />
      <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary">Audio de reafirmación</h1>
            <p className="text-sm text-muted-foreground">
              {audioState.user?.name || 'Alumno'}
            </p>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <Card className="border-cyan-200 bg-cyan-50/60 dark:bg-cyan-950/20 dark:border-cyan-900">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle>Estado del audio</CardTitle>
                <CardDescription>
                  El audio es opcional y este link queda disponible aunque el formulario esté finalizado.
                </CardDescription>
              </div>
              <Badge variant="outline">{getAudioStatusLabel(audioState.status)}</Badge>
            </div>
          </CardHeader>
        </Card>

        {hasUploadedAudio ? (
          <>
            <Card className="border-green-200 bg-green-50/70 dark:bg-green-950/20 dark:border-green-900">
              <CardHeader className="text-center">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
                <CardTitle>Audio cargado</CardTitle>
                <CardDescription>
                  Ya se cargó el audio. Podés continuar en la computadora.
                </CardDescription>
              </CardHeader>
            </Card>
            <RmPasswordSetup
              email={audioState.user?.email}
              configured={Boolean(audioState.rmc_password_configured)}
              onSubmit={handleCreateRmPassword}
              onConfigured={() => setAudioState((current) => ({
                ...current,
                rmc_password_configured: true,
              }))}
            />
          </>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Frases</CardTitle>
                <CardDescription>
                  Guardá de 3 a 6 frases. Después usá el texto completo para grabar.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md border bg-muted/40 p-4 space-y-3 text-sm">
                  <p className="font-medium">Inicio:</p>
                  <p>{AFFIRMATION_AUDIO_INTRO}</p>
                  <p className="font-medium">Cierre:</p>
                  <p>{AFFIRMATION_AUDIO_OUTRO}</p>
                </div>
                {Array.from({ length: MAX_PHRASES }).map((_, index) => (
                  <div key={index} className="space-y-2">
                    <Label htmlFor={`audio-page-phrase-${index}`}>
                      Frase {index + 1}{index >= 3 ? ' (opcional)' : ''}
                    </Label>
                    <Textarea
                      id={`audio-page-phrase-${index}`}
                      value={phrases[index] || ''}
                      onChange={(event) => updatePhrase(index, event.target.value)}
                      rows={2}
                      maxLength={280}
                      placeholder={`Escribí tu frase ${index + 1}. Ej: Soy constante con lo que me propongo.`}
                    />
                  </div>
                ))}
                <Button type="button" onClick={handleSavePhrases} disabled={isSaving}>
                  {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar frases
                </Button>
              </CardContent>
            </Card>

            {cleanPhrases.length >= 3 && (
              <>
                <Card className="hidden sm:block">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="flex items-center gap-2">
                        <Volume2 className="w-5 h-5 text-primary" />
                        Texto para grabar
                      </CardTitle>
                      <Button type="button" variant="outline" size="sm" onClick={handleCopyScript}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copiar
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="whitespace-pre-line text-sm leading-6 rounded-md border bg-muted/30 p-4">
                      {scriptText}
                    </div>
                  </CardContent>
                </Card>

                <div className="hidden sm:block">
                  <AudioQrCard audioUrl={audioUrl} />
                </div>

                <AffirmationAudioRecorder
                  token={token}
                  scriptText={scriptText}
                  onUploaded={handleUploaded}
                />
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default AudioPage;
